import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Alert, AlertDescription } from '../ui/alert';
import { Eye, EyeOff, UserPlus, Mail, Lock, User, Building } from 'lucide-react';
import { toast } from 'sonner';
import { GoogleLogin } from '@react-oauth/google';
import axios from 'axios';
import Cookies from 'js-cookie';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const RegisterForm = ({ onToggleMode }) => {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    full_name: '',
    company_name: ''
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (error) setError('');
  };

  const validateForm = () => {
    if (!formData.email.trim() || !formData.password.trim() || !formData.full_name.trim()) {
      setError('Please fill in all required fields');
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('=== REGISTER FORM SUBMIT ===');
    
    if (!validateForm()) {
      console.log('Validation failed');
      return;
    }

    setLoading(true);
    setError(''); // Clear any previous errors
    console.log('Calling register with email:', formData.email);
    
    try {
      const result = await register({
        email: formData.email,
        password: formData.password,
        full_name: formData.full_name,
        company_name: formData.company_name || null
      });
      
      console.log('Register result:', result);
      
      if (result.success) {
        console.log('Registration successful!');
        toast.success('Account created! Please check your email to verify your account.');
        // Redirect to verification pending page with email
        setTimeout(() => navigate(`/verification-pending?email=${encodeURIComponent(formData.email)}`), 1000);
      } else {
        // Customize error message for better user experience
        let errorMessage = result.error || 'Registration failed';
        
        // Check for specific error messages and make them more user-friendly
        if (errorMessage.toLowerCase().includes('email already registered')) {
          errorMessage = 'This email is already registered. Please use a different email or try logging in.';
        } else if (errorMessage.toLowerCase().includes('unable to connect')) {
          errorMessage = 'Unable to connect to server. Please check your internet connection and try again.';
        }
        
        console.log('Registration failed with error:', errorMessage);
        setError(errorMessage);
        toast.error(errorMessage);
      }
    } catch (err) {
      console.error('Registration exception:', err);
      let errorMessage = 'Registration failed. Please try again.';
      
      // Try to extract more specific error message
      if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
        if (errorMessage.toLowerCase().includes('email already registered')) {
          errorMessage = 'This email is already registered. Please use a different email or try logging in.';
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      console.log('Setting error:', errorMessage);
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
      console.log('Registration complete, loading state reset');
    }
  };

  // Google Sign-In handler for registration
  const handleGoogleSuccess = async (credentialResponse) => {
    console.log('Google Sign-In Success (Registration):', credentialResponse);
    setLoading(true);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/google`, {
        token: credentialResponse.credential
      });
      
      if (response.data.access_token) {
        // Store token and user data
        Cookies.set('enakid_token', response.data.access_token, { expires: 7 });
        Cookies.set('enakid_user', JSON.stringify(response.data.user), { expires: 7 });
        localStorage.setItem('enakid_token', response.data.access_token);
        localStorage.setItem('enakid_user', JSON.stringify(response.data.user));
        
        toast.success('Google sign-up successful! Welcome to TavPlans!');
        setTimeout(() => {
          window.location.href = '/dashboard';
        }, 500);
      }
    } catch (err) {
      console.error('Google sign-up error:', err);
      const errorMessage = err.response?.data?.detail || 'Google sign-up failed. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleError = () => {
    console.log('Google Sign-In Error (Registration)');
    toast.error('Google sign-up failed. Please try again.');
  };

  return (
    <Card className="w-full max-w-md glass-card shadow-strong">
      <CardHeader className="text-center">
        <img 
          src="/TavPlans_Logo.png" 
          alt="TavPlans Logo" 
          className="h-16 w-16 object-contain mx-auto mb-4"
        />
        <CardTitle className="text-2xl font-bold">Create Account</CardTitle>
        <CardDescription>
          Join TavPlans to start building comprehensive financial models for your business
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          
          <div className="space-y-2">
            <Label htmlFor="full_name">Full Name *</Label>
            <div className="relative">
              <Input
                id="full_name"
                type="text"
                placeholder="John Doe"
                value={formData.full_name}
                onChange={(e) => handleInputChange('full_name', e.target.value)}
                className="pl-10 form-input"
                disabled={loading}
              />
              <User className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email Address *</Label>
            <div className="relative">
              <Input
                id="email"
                type="email"
                placeholder="your@email.com"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className="pl-10 form-input"
                disabled={loading}
              />
              <Mail className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="company_name">Company Name (Optional)</Label>
            <div className="relative">
              <Input
                id="company_name"
                type="text"
                placeholder="Your Company Ltd"
                value={formData.company_name}
                onChange={(e) => handleInputChange('company_name', e.target.value)}
                className="pl-10 form-input"
                disabled={loading}
              />
              <Building className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Password *</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter your password"
                value={formData.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                className="pl-10 pr-10 form-input"
                disabled={loading}
              />
              <Lock className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-3"
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4 text-slate-400" />
                ) : (
                  <Eye className="h-4 w-4 text-slate-400" />
                )}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPassword">Confirm Password *</Label>
            <div className="relative">
              <Input
                id="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder="Confirm your password"
                value={formData.confirmPassword}
                onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                className="pl-10 pr-10 form-input"
                disabled={loading}
              />
              <Lock className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-3"
              >
                {showConfirmPassword ? (
                  <EyeOff className="h-4 w-4 text-slate-400" />
                ) : (
                  <Eye className="h-4 w-4 text-slate-400" />
                )}
              </button>
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full btn-primary"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Creating Account...
              </>
            ) : (
              <>
                <UserPlus className="h-4 w-4 mr-2" />
                Create Account
              </>
            )}
          </Button>
        </form>

        {/* Social Registration Options */}
        <div className="mt-6">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">Or sign up with</span>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-2 gap-3">
            <div className="col-span-1">
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleError}
                useOneTap
                theme="outline"
                size="large"
                text="signup_with"
                shape="rectangular"
                logo_alignment="left"
              />
            </div>

            <button
              type="button"
              onClick={() => {
                // TODO: Implement Telegram OAuth
                alert('Telegram authentication coming soon!');
              }}
              className="w-full inline-flex justify-center items-center py-2 px-4 border border-gray-300 rounded-lg shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="#0088cc">
                <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221l-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.446 1.394c-.14.18-.357.295-.6.295-.002 0-.003 0-.005 0l.213-3.054 5.56-5.022c.24-.213-.054-.334-.373-.121l-6.869 4.326-2.96-.924c-.64-.203-.658-.64.135-.954l11.566-4.458c.538-.196 1.006.128.832.941z"/>
              </svg>
              Telegram
            </button>
          </div>
        </div>

        <div className="mt-6 text-center">
          <p className="text-sm text-slate-600">
            Already have an account?{' '}
            <button
              onClick={onToggleMode}
              className="font-medium text-blue-600 hover:text-blue-500"
            >
              Sign In
            </button>
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default RegisterForm;